import { useMemo, useState } from 'react';
import {
  Modal,
  Dropdown,
  FormLabel,
  FormItem,
  TextField,
  FormHelperText,
  TextArea,
  Typography,
  Icon,
  IconButton,
} from '@getsynapse/design-system';
import intl from 'react-intl-universal';
import { REASONS_OF_DECLINATION } from 'utils/constants';

type DeclineRequestModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onDeclineRequest: ({
    reason,
    message,
    comments,
  }: {
    reason: string;
    message?: string | undefined;
    comments?: string | undefined;
  }) => void;
};

const DeclineRequestModal = ({
  isOpen,
  onCancel,
  onDeclineRequest,
}: DeclineRequestModalProps) => {
  const other = REASONS_OF_DECLINATION[REASONS_OF_DECLINATION.length - 1];
  const [data, setData] = useState<{
    reason: typeof REASONS_OF_DECLINATION[number] | '';
    specificReason?: string;
    reasonError: boolean;
    specificReasonError: boolean;
    comments: string;
  }>({
    reason: '',
    specificReason: '',
    reasonError: false,
    specificReasonError: false,
    comments: '',
  });
  const reasonOptions = useMemo(() => {
    return REASONS_OF_DECLINATION.map((reason) => ({
      value: reason,
      label: intl.get(
        `REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.REASONS.${reason}`
      ),
    }));
  }, []);

  const handleClose = () => {
    onCancel();
    setData((prevData) => ({
      ...prevData,
      reason: '',
      specificReason: '',
      comments: '',
    }));
  };

  const handleDeclineRequest = () => {
    if (!data.reason) {
      setData((prevData) => ({ ...prevData, reasonError: true }));
      return;
    }
    if (data.reason === other && !data.specificReason) {
      setData((prevData) => ({ ...prevData, specificReasonError: true }));
      return;
    }
    setData((prevData) => ({
      ...prevData,
      reasonError: false,
      specificReasonError: false,
    }));
    onDeclineRequest({
      reason: data.reason,
      message: data.specificReason,
      comments: data.comments,
    });
  };

  return (
    <Modal
      childrenClassName='pr-0'
      aria-label='decline-request-modal'
      actionButtons={[
        {
          children: intl.get(
            'REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.DECLINATION'
          ),
          variant: 'primary',
          onClick: handleDeclineRequest,
          'data-cy': 'request-declination-button',
        },
        {
          children: intl.get(
            'REQUESTS_LIST_PAGE.MODAL.DELETE_REQUEST.CANCEL_BUTTON'
          ),
          variant: 'tertiary',
          onClick: handleClose,
        },
      ]}
      closeModal={handleClose}
      isOpen={isOpen}
      size='medium'
    >
      <div className='relative mb-4'>
        <IconButton
          onClick={handleClose}
          className='absolute top-0 right-0 text-primary-dark'
          aria-label={intl.get('CLOSE') || 'Close'}
          data-cy='close-modal-button'
          name='close'
        />
        <div className='flex items-center gap-2'>
          <Icon name='close-circle' className='w-5 h-5 text-olive-green' />
          <Typography
            className='text-lg font-normal leading-[28px]'
            data-cy='change-form-button'
          >
            {intl.get(
              'REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.DECLINE_REQUEST'
            )}
          </Typography>
        </div>
      </div>
      <FormLabel state={data.reasonError ? 'error' : 'default'} required>
        {intl.get(
          'REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.DECLINATION_REASON'
        )}
      </FormLabel>
      <Dropdown
        options={reasonOptions}
        onChange={(value) =>
          setData((prevData) => ({
            ...prevData,
            reason: value.value,
            reasonError: false,
            specificReason: '',
          }))
        }
        placeholder={intl.get(
          'REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.REASON'
        )}
        state={data.reasonError ? 'error' : 'default'}
        triggerProps={{ 'data-cy': 'declination-reason-dropdown' }}
      />
      {data.reasonError && (
        <FormHelperText state='error'>
          {intl.get('REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.ERROR')}
        </FormHelperText>
      )}
      {data.reason === other && (
        <FormItem
          label={intl.get('REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.SPECIFY')}
          labelProps={{
            state: data.specificReasonError ? 'error' : 'default',
            required: true,
          }}
          className='mt-4'
        >
          <TextField
            variant='text'
            length='medium'
            placeholder={intl.get(
              'REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.SPECIFY_OTHER'
            )}
            onChange={(event: any) =>
              setData((prevData) => ({
                ...prevData,
                specificReason: event.target.value,
                specificReasonError: false,
              }))
            }
            defaultValue={data.specificReason}
            className='focus:ring-0'
            data-cy='other-specification-input__decline'
            state={data.specificReasonError ? 'error' : 'default'}
            helpText={
              data.specificReasonError
                ? intl.get('REQUEST_PAGE.BASIC_DETAILS.ERRORS.MISSING_INPUT')
                : ''
            }
          />
        </FormItem>
      )}
      <TextArea
        className='pt-4 flex flex-col'
        label={intl.get('REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.COMMENTS')}
        textAreaProps={{
          className: 'h-28',
          placeholder: intl.get(
            'REQUESTS_LIST_PAGE.MODAL.DECLINE_REQUEST.DECLINATION_DETAILS'
          ),
        }}
        value={data.comments}
        onChange={(event) =>
          setData((prevData) => ({ ...prevData, comments: event.target.value }))
        }
      />
    </Modal>
  );
};

export default DeclineRequestModal;
